import React, { useEffect, useRef, useState, useCallback } from 'react';
import { storedLang } from '../../i18n/LangContext';   // V147 중국어 화면이면 중국어 (캔버스 글자)

const W = 360;
const H = 520;
const GRAVITY   = 0.195;
const FLAP      = -3.315;
const PIPE_W    = 52;
const PIPE_GAP  = 155;
const PIPE_SPEE = 2.6;
const BIRD_R    = 22;
const FOODS     = ['🍙','🍜','🥢','🥬','🧅','🥕','🫙','🍳'];
const BEST_KEY  = 'ddabak_egg_best_food';
const LEGACY_KEY = 'ddabak_egg_best'; // 기존 사용자 호환

// V147: 게임 글자 (로고를 누르면 열린다 — 중국 직원도 누른다)
const TXT = {
  ko: { title: '🍱 셈셈셈!', start: '클릭 또는 스페이스바로 시작', goal: '장애물을 피해 멀리 날아가세요!', over: '게임 오버 🥲', score: '점수', best: '최고',
        retry: '다시 도전 🔄', bestRec: '🏆 최고 기록', hint: '클릭 / 탭 / 스페이스바' },
  zh: { title: '🍱 SEMFIT!', start: '点击或按空格键开始', goal: '躲开障碍物，飞得越远越好！', over: '游戏结束 🥲', score: '得分', best: '最高',
        retry: '再来一次 🔄', bestRec: '🏆 最高纪录', hint: '点击 / 轻触 / 空格键' },
};
const txt = () => TXT[storedLang() === 'zh' ? 'zh' : 'ko'];

function randomFood() { return FOODS[Math.floor(Math.random() * FOODS.length)]; }

/* ── 이모지 캔버스 렌더 헬퍼 ── */
function drawEmoji(ctx, emoji, x, y, size = 36) {
  ctx.save();
  ctx.font = `${size}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, x, y);
  ctx.restore();
}

function readBest() {
  return parseInt(
    localStorage.getItem(BEST_KEY) ||
    localStorage.getItem(LEGACY_KEY) ||
    '0'
  );
}

export default function FlappyFood({ onClose }) {
  const canvasRef = useRef(null);
  const state     = useRef(null);   // mutable game state
  const rafRef    = useRef(null);
  const [phase, setPhase]       = useState('idle');   // idle | playing | dead
  const [score, setScore]       = useState(0);
  const [best,  setBest]        = useState(readBest);

  /* ── 게임 상태 초기화 ── */
  const initState = useCallback(() => ({
    bird: { y: H / 2, vy: 0, angle: 0 },
    pipes: [],
    particles: [],
    frame: 0,
    score: 0,
    passedIds: new Set(),
  }), []);

  /* ── 파이프 생성 ── */
  function makePipe(x) {
    const top = 80 + Math.random() * (H - PIPE_GAP - 140);
    return { id: Math.random(), x, top, food: randomFood(), collected: false };
  }

  /* ── 점수 파티클 ── */
  function spawnParticles(x, y) {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      arr.push({
        x, y,
        vx: (Math.random() - 0.5) * 5,
        vy: -2 - Math.random() * 3,
        life: 1, text: '+1',
      });
    }
    return arr;
  }

  /* ── 점프 ── */
  const flap = useCallback(() => {
    if (phase === 'idle') {
      setPhase('playing');
      state.current.bird.vy = FLAP;
      return;
    }
    if (phase === 'playing') {
      state.current.bird.vy = FLAP;
    }
    if (phase === 'dead') {
      state.current = initState();
      setScore(0);
      setPhase('playing');
    }
  }, [phase, initState]);

  /* ── 키/터치 이벤트 ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') { e.preventDefault(); flap(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flap]);

  /* ── 게임 루프 ── */
  useEffect(() => {
    if (!state.current) state.current = initState();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let currentPhase = phase;

    function loop() {
      const s = state.current;
      const { bird, pipes, particles } = s;
      ctx.clearRect(0, 0, W, H);

      // ── 배경
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#e8f5e9');
      bg.addColorStop(1, '#c8e6c9');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // ── 바닥
      ctx.fillStyle = '#FF6F0F';
      ctx.fillRect(0, H - 40, W, 40);
      ctx.fillStyle = '#388e3c';
      ctx.fillRect(0, H - 40, W, 6);
      // 잔디 패턴
      for (let gx = 0; gx < W; gx += 18) {
        ctx.fillStyle = '#2e7d32';
        ctx.beginPath();
        ctx.moveTo(gx, H - 40);
        ctx.lineTo(gx + 6, H - 52);
        ctx.lineTo(gx + 12, H - 40);
        ctx.fill();
      }

      // ── 구름
      [{ x: 60, y: 60, r: 1 }, { x: 200, y: 40, r: 0.8 }, { x: 310, y: 80, r: 0.7 }].forEach(c => {
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.beginPath();
        ctx.arc(c.x, c.y, 28 * c.r, 0, Math.PI * 2);
        ctx.arc(c.x + 22 * c.r, c.y - 6 * c.r, 20 * c.r, 0, Math.PI * 2);
        ctx.arc(c.x + 40 * c.r, c.y, 22 * c.r, 0, Math.PI * 2);
        ctx.fill();
      });

      if (currentPhase === 'playing') {
        // ── 새 물리
        bird.vy += GRAVITY;
        bird.y  += bird.vy;
        bird.angle = Math.max(-0.5, Math.min(1.2, bird.vy * 0.06));

        // ── 파이프 생성
        s.frame++;
        if (s.frame % 90 === 0 || pipes.length === 0) {
          pipes.push(makePipe(W + 20));
        }

        // ── 파이프 이동 & 충돌
        let died = false;
        for (let i = pipes.length - 1; i >= 0; i--) {
          pipes[i].x -= PIPE_SPEE;
          if (pipes[i].x + PIPE_W < 0) { pipes.splice(i, 1); continue; }

          const px = pipes[i].x, pt = pipes[i].top, pb = pt + PIPE_GAP;
          const bx = 80, by = bird.y;

          // 점수 통과 체크
          if (!s.passedIds.has(pipes[i].id) && bx > px + PIPE_W / 2) {
            s.passedIds.add(pipes[i].id);
            s.score++;
            setScore(s.score);
            // 파티클
            s.particles.push(...spawnParticles(bx + 30, by));
          }

          // 충돌 (사각형 근사)
          const hitX = bx + BIRD_R * 0.6 > px && bx - BIRD_R * 0.6 < px + PIPE_W;
          const hitY = by - BIRD_R * 0.7 < pt || by + BIRD_R * 0.7 > pb;
          if (hitX && hitY) died = true;
        }

        // 바닥/천장 충돌
        if (bird.y + BIRD_R > H - 40 || bird.y - BIRD_R < 0) died = true;

        if (died) {
          currentPhase = 'dead';
          setPhase('dead');
          setBest(prev => {
            const next = Math.max(prev, s.score);
            localStorage.setItem(BEST_KEY, String(next));
            return next;
          });
        }

        // ── 파티클 업데이트
        for (let i = particles.length - 1; i >= 0; i--) {
          const p = particles[i];
          p.x += p.vx; p.y += p.vy; p.vy += 0.15; p.life -= 0.06;
          if (p.life <= 0) particles.splice(i, 1);
        }
      }

      // ── 파이프 그리기
      pipes.forEach(p => {
        // 상단 파이프
        const grad = ctx.createLinearGradient(p.x, 0, p.x + PIPE_W, 0);
        grad.addColorStop(0, '#2e7d32');
        grad.addColorStop(0.4, '#43a047');
        grad.addColorStop(1, '#1b5e20');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(p.x - 4, 0, PIPE_W + 8, p.top - 10, [0, 0, 8, 8]);
        ctx.fill();
        ctx.fillRect(p.x, 0, PIPE_W, p.top);
        // 파이프 캡
        ctx.fillStyle = '#1b5e20';
        ctx.beginPath();
        ctx.roundRect(p.x - 6, p.top - 18, PIPE_W + 12, 18, 6);
        ctx.fill();

        // 하단 파이프
        ctx.fillStyle = grad;
        ctx.fillRect(p.x, p.top + PIPE_GAP, PIPE_W, H - p.top - PIPE_GAP);
        ctx.beginPath();
        ctx.roundRect(p.x - 4, p.top + PIPE_GAP, PIPE_W + 8, H, [8, 8, 0, 0]);
        ctx.fill();
        // 파이프 캡
        ctx.fillStyle = '#1b5e20';
        ctx.beginPath();
        ctx.roundRect(p.x - 6, p.top + PIPE_GAP, PIPE_W + 12, 18, 6);
        ctx.fill();

        // 음식 이모지 (파이프 사이 중앙)
        drawEmoji(ctx, p.food, p.x + PIPE_W / 2, p.top + PIPE_GAP / 2, 28);
      });

      // ── 도시락 그리기
      ctx.save();
      ctx.translate(80, bird.y);
      ctx.rotate(bird.angle);

      const wingFlap = Math.sin(s.frame * 0.35) * 0.45; // 날개 펄럭 각도
      const wingSizeX = 28;
      const wingSizeY = 18;

      // ── 왼쪽 천사 날개
      ctx.save();
      ctx.translate(-18, -4);
      ctx.rotate(-0.3 - wingFlap);
      // 날개 깃털 3단 겹침
      [0, 0.18, 0.34].forEach((rot, i) => {
        ctx.save();
        ctx.rotate(-rot);
        const g = ctx.createRadialGradient(-4, -2, 2, -4, -2, wingSizeX - i * 4);
        g.addColorStop(0, 'rgba(255,255,255,1)');
        g.addColorStop(0.6, 'rgba(230,245,255,0.92)');
        g.addColorStop(1, 'rgba(200,230,255,0.35)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(
          -(wingSizeX - i * 5), -(wingSizeY + i * 3),
          -(wingSizeX + 4 - i * 3), (wingSizeY / 2 - i * 2),
          0, wingSizeY / 3
        );
        ctx.closePath();
        ctx.fill();
        // 날개 테두리
        ctx.strokeStyle = 'rgba(180,210,255,0.6)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
        ctx.restore();
      });
      ctx.restore();

      // ── 오른쪽 천사 날개 (좌우 반전)
      ctx.save();
      ctx.translate(18, -4);
      ctx.scale(-1, 1);
      ctx.rotate(-0.3 - wingFlap);
      [0, 0.18, 0.34].forEach((rot, i) => {
        ctx.save();
        ctx.rotate(-rot);
        const g = ctx.createRadialGradient(-4, -2, 2, -4, -2, wingSizeX - i * 4);
        g.addColorStop(0, 'rgba(255,255,255,1)');
        g.addColorStop(0.6, 'rgba(230,245,255,0.92)');
        g.addColorStop(1, 'rgba(200,230,255,0.35)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(
          -(wingSizeX - i * 5), -(wingSizeY + i * 3),
          -(wingSizeX + 4 - i * 3), (wingSizeY / 2 - i * 2),
          0, wingSizeY / 3
        );
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(180,210,255,0.6)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
        ctx.restore();
      });
      ctx.restore();

      // ── 후광 (헤일로)
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(0, -26, 16, 5, 0, 0, Math.PI * 2);
      const halo = ctx.createLinearGradient(-16, -26, 16, -26);
      halo.addColorStop(0,   'rgba(255,220,50,0)');
      halo.addColorStop(0.3, 'rgba(255,220,50,0.9)');
      halo.addColorStop(0.7, 'rgba(255,220,50,0.9)');
      halo.addColorStop(1,   'rgba(255,220,50,0)');
      ctx.strokeStyle = halo;
      ctx.lineWidth = 3.5;
      ctx.stroke();
      ctx.restore();

      // ── 도시락 이모지 (선명하게, 그림자 없음)
      drawEmoji(ctx, '🍱', 0, 2, 44);

      ctx.restore();

      // ── 파티클
      particles.forEach(p => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = '#FF8F00';
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(p.text, p.x, p.y);
        ctx.globalAlpha = 1;
      });

      // ── 점수 표시 (playing / dead)
      if (currentPhase !== 'idle') {
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.font = 'bold 38px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(s.score), W / 2 + 2, 64);
        ctx.fillStyle = '#fff';
        ctx.fillText(String(s.score), W / 2, 62);
      }

      // ── 오버레이
      if (currentPhase === 'idle') {
        ctx.fillStyle = 'rgba(0,0,0,0.35)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 34px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(txt().title, W / 2, H / 2 - 40);
        ctx.font = '18px sans-serif';
        ctx.fillStyle = '#E4E4E7';
        ctx.fillText(txt().start, W / 2, H / 2 + 4);
        ctx.font = '14px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fillText(txt().goal, W / 2, H / 2 + 32);
      }

      if (currentPhase === 'dead') {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, 0, W, H);

        // 게임 오버 카드
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 130, H / 2 - 110, 260, 210, 20);
        ctx.fill();

        ctx.fillStyle = '#FF6F0F';
        ctx.font = 'bold 26px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(txt().over, W / 2, H / 2 - 65);

        ctx.fillStyle = '#374151';
        ctx.font = 'bold 16px sans-serif';
        ctx.fillText(txt().score, W / 2 - 55, H / 2 - 28);
        ctx.fillText(txt().best, W / 2 + 55, H / 2 - 28);

        ctx.font = 'bold 38px sans-serif';
        ctx.fillStyle = '#FF6F0F';
        ctx.fillText(String(s.score), W / 2 - 55, H / 2 + 18);
        ctx.fillStyle = '#FF8F00';
        ctx.fillText(String(Math.max(s.score, readBest())), W / 2 + 55, H / 2 + 18);

        ctx.fillStyle = '#FF6F0F';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 90, H / 2 + 44, 180, 42, 12);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 17px sans-serif';
        ctx.fillText(txt().retry, W / 2, H / 2 + 70);
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, initState]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '12px',
      }}
      onClick={flap}
    >
      {/* 닫기 버튼 */}
      <button
        onClick={e => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute', top: '16px', right: '20px',
          background: 'rgba(255,255,255,0.2)', border: 'none',
          color: '#fff', fontSize: '22px', cursor: 'pointer',
          borderRadius: '8px', padding: '4px 10px', fontWeight: '700',
          backdropFilter: 'blur(4px)',
        }}>
        ✕
      </button>

      {/* 최고 점수 */}
      {best > 0 && (
        <div style={{ color: '#D4D4D8', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
          {txt().bestRec}: {best}
        </div>
      )}

      {/* 캔버스 */}
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'block',
          maxWidth: '95vw',
          maxHeight: '80vh',
          objectFit: 'contain',
        }}
      />

      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '4px' }}>
        {txt().hint}
      </p>
    </div>
  );
}
